import { useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2, Upload, X, CheckCircle2, FileText, Plus } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { PageHero } from "../components/common";
import { COMPANY } from "../lib/constants";

const inputCls = "w-full border border-gray-300 rounded-md px-3.5 py-2 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors text-sm";
const labelCls = "block text-sm font-medium text-charcoal-700 mb-1";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const YN = ["Yes", "No"];

function Field({ label, children }) { return <div><label className={labelCls}>{label}</label>{children}</div>; }
function Section({ title, desc, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-6 sm:p-8">
      <h3 className="text-xl font-semibold text-charcoal-900 font-display">{title}</h3>
      {desc && <p className="text-sm text-charcoal-500 mt-1 mb-5">{desc}</p>}
      <div className={desc ? "" : "mt-5"}>{children}</div>
    </div>
  );
}
function Radio({ value, onChange, name, options = YN }) {
  return (
    <div className="flex gap-4 mt-1">
      {options.map((o) => (
        <label key={o} className="flex items-center gap-1.5 text-sm text-charcoal-700 cursor-pointer">
          <input type="radio" name={name} checked={value === o} onChange={() => onChange(o)} className="accent-[#22b455]" /> {o}
        </label>
      ))}
    </div>
  );
}

const emptyEmployer = { company: "", supervisor: "", phone: "", title: "", start: "", end: "", start_pay: "", end_pay: "", responsibilities: "", reason: "", contact_ok: "Yes" };
const emptyRef = { name: "", relationship: "", company: "", phone: "", email: "", years_known: "" };

export default function Employment() {
  const [gate, setGate] = useState({ pw: "", loading: false, ok: false });
  const [d, setD] = useState({
    full_legal_name: "", preferred_name: "", phone: "", email: "", address: "", city: "", state: "", zip: "",
    dob: "", preferred_contact: "Phone",
    position: "", desired_hours: "", employment_type: "Full-time", start_date: "", desired_pay: "",
    willing_travel: "Yes", reliable_transportation: "Yes", drivers_license: "Yes", authorized_us: "Yes",
    background_check: "Yes", drug_screening: "Yes",
    weekend_availability: "Yes", holiday_availability: "Yes", unavailable_days: "",
    cleaning_experience: "", commercial_experience: "", work_independently: "Yes", work_around_people: "Yes",
    lift_40: "Yes", comfortable_chemicals: "Yes", why_join: "", anything_else: "",
  });
  const [availability, setAvailability] = useState(DAYS.reduce((a, day) => ({ ...a, [day]: { start: "", end: "", off: false } }), {}));
  const [employers, setEmployers] = useState([{ ...emptyEmployer }, { ...emptyEmployer }, { ...emptyEmployer }]);
  const [refs, setRefs] = useState([{ ...emptyRef }, { ...emptyRef }, { ...emptyRef }]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const verify = async (e) => {
    e.preventDefault();
    setGate((g) => ({ ...g, loading: true }));
    try {
      await api.post("/applications/verify-password", { password: gate.pw });
      setGate((g) => ({ ...g, ok: true, loading: false }));
    } catch (err) { toast.error(apiErr(err)); setGate((g) => ({ ...g, loading: false })); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
      } catch (err) { toast.error(`${file.name}: ${apiErr(err)}`); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!d.full_legal_name || !d.email || !d.phone || !d.position) { toast.error("Please complete name, email, phone, and position."); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (!consent || !signature.trim()) { toast.error("Please provide your electronic signature and consent."); return; }
    setSubmitting(true);
    try {
      const payload = {
        data: { ...d, availability, employment_history: employers, references: refs, date_signed: new Date().toISOString().slice(0, 10) },
        attachments, consent, signature,
      };
      await api.post("/applications", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiErr(err)); }
    setSubmitting(false);
  };

  if (!gate.ok) {
    return (
      <div data-testid="employment-gate">
        <PageHero eyebrow="Careers" title="Employment Application"
          subtitle="This application is password protected. Please enter the password provided to you to continue." />
        <section className="bg-[#f8f9fa]">
          <div className="ubm-container py-20 max-w-md mx-auto">
            <form onSubmit={verify} className="bg-white border border-gray-200 rounded-md p-8">
              <Lock className="text-primary mb-4" size={30} />
              <h2 className="text-xl font-semibold text-charcoal-900">Enter application password</h2>
              <p className="text-sm text-charcoal-500 mt-1 mb-5">Don't have a password? Contact us at {COMPANY.phoneDisplay}.</p>
              <input type="password" className={inputCls} placeholder="Password" value={gate.pw} onChange={(e) => setGate((g) => ({ ...g, pw: e.target.value }))} data-testid="app-password-input" />
              <button type="submit" disabled={gate.loading} className="mt-4 w-full bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="app-password-submit">
                {gate.loading ? "Checking…" : "Unlock Application"}
              </button>
            </form>
          </div>
        </section>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="employment-success">
        <PageHero eyebrow="Careers" title="Application Submitted" />
        <section className="bg-[#f8f9fa]">
          <div className="ubm-container py-20 max-w-lg mx-auto text-center">
            <div className="bg-white border border-primary/30 rounded-md p-10">
              <CheckCircle2 className="text-primary mx-auto mb-4" size={52} />
              <h2 className="text-2xl font-bold text-charcoal-900">Thank you for applying!</h2>
              <p className="mt-2 text-charcoal-600">We've received your application and sent a confirmation to your email. Our team will review it and reach out if we move forward.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="employment-form">
      <PageHero eyebrow="Careers" title="Employment Application"
        subtitle="We're excited you're interested in joining United Building Maintenance. Please complete all applicable fields." />
      <section className="bg-[#f8f9fa]">
        <form onSubmit={submit} className="ubm-container py-14 max-w-4xl space-y-6">
          <Section title="Personal Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Legal Name *"><input className={inputCls} value={d.full_legal_name} onChange={(e) => set("full_legal_name", e.target.value)} data-testid="app-name" /></Field>
              <Field label="Preferred Name"><input className={inputCls} value={d.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} /></Field>
              <Field label="Phone Number *"><input className={inputCls} value={d.phone} onChange={(e) => set("phone", e.target.value)} data-testid="app-phone" /></Field>
              <Field label="Email Address *"><input type="email" className={inputCls} value={d.email} onChange={(e) => set("email", e.target.value)} data-testid="app-email" /></Field>
              <Field label="Home Address"><input className={inputCls} value={d.address} onChange={(e) => set("address", e.target.value)} /></Field>
              <Field label="City"><input className={inputCls} value={d.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="State"><input className={inputCls} value={d.state} onChange={(e) => set("state", e.target.value)} /></Field>
              <Field label="ZIP Code"><input className={inputCls} value={d.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
              <Field label="Date of Birth (optional)"><input type="date" className={inputCls} value={d.dob} onChange={(e) => set("dob", e.target.value)} /></Field>
              <Field label="Preferred Contact Method">
                <select className={inputCls} value={d.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)}>
                  {["Phone", "Email", "Text"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
            </div>
          </Section>

          <Section title="Position Information">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Position Applying For *"><input className={inputCls} value={d.position} onChange={(e) => set("position", e.target.value)} data-testid="app-position" placeholder="e.g. Cleaning Technician" /></Field>
              <Field label="Desired Number of Hours"><input className={inputCls} value={d.desired_hours} onChange={(e) => set("desired_hours", e.target.value)} /></Field>
              <Field label="Full-time or Part-time">
                <select className={inputCls} value={d.employment_type} onChange={(e) => set("employment_type", e.target.value)}>
                  {["Full-time", "Part-time", "Either"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Available Start Date"><input type="date" className={inputCls} value={d.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
              <Field label="Desired Pay"><input className={inputCls} value={d.desired_pay} onChange={(e) => set("desired_pay", e.target.value)} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                ["Are you willing to travel?", "willing_travel"],
                ["Do you have reliable transportation?", "reliable_transportation"],
                ["Do you have a valid driver's license?", "drivers_license"],
                ["Are you legally authorized to work in the U.S.?", "authorized_us"],
                ["Are you willing to undergo a background check?", "background_check"],
                ["Willing to undergo drug screening if required?", "drug_screening"],
              ].map(([label, key]) => (
                <Field key={key} label={label}><Radio name={key} value={d[key]} onChange={(v) => set(key, v)} /></Field>
              ))}
            </div>
          </Section>

          <Section title="Availability" desc="Enter your available times for each day of the week.">
            <div className="space-y-2">
              {DAYS.map((day) => (
                <div key={day} className="grid grid-cols-12 gap-2 items-center">
                  <span className="col-span-4 sm:col-span-2 text-sm font-medium text-charcoal-800">{day}</span>
                  <input type="time" className={`${inputCls} col-span-4 sm:col-span-3`} value={availability[day].start} disabled={availability[day].off}
                    onChange={(e) => setAvailability((a) => ({ ...a, [day]: { ...a[day], start: e.target.value } }))} />
                  <input type="time" className={`${inputCls} col-span-4 sm:col-span-3`} value={availability[day].end} disabled={availability[day].off}
                    onChange={(e) => setAvailability((a) => ({ ...a, [day]: { ...a[day], end: e.target.value } }))} />
                  <label className="col-span-12 sm:col-span-4 flex items-center gap-1.5 text-sm text-charcoal-600">
                    <input type="checkbox" className="accent-[#22b455]" checked={availability[day].off}
                      onChange={(e) => setAvailability((a) => ({ ...a, [day]: { ...a[day], off: e.target.checked } }))} /> Unavailable
                  </label>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Available on weekends?"><Radio name="weekend" value={d.weekend_availability} onChange={(v) => set("weekend_availability", v)} /></Field>
              <Field label="Available on holidays?"><Radio name="holiday" value={d.holiday_availability} onChange={(v) => set("holiday_availability", v)} /></Field>
            </div>
          </Section>

          <Section title="Employment History" desc="Please list your three most recent employers.">
            <div className="space-y-6">
              {employers.map((emp, i) => (
                <div key={i} className="border border-gray-200 rounded-md p-4">
                  <div className="text-sm font-semibold text-charcoal-900 mb-3">Employer {i + 1}</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Company Name"><input className={inputCls} value={emp.company} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, company: e.target.value } : x))} /></Field>
                    <Field label="Supervisor Name"><input className={inputCls} value={emp.supervisor} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, supervisor: e.target.value } : x))} /></Field>
                    <Field label="Phone Number"><input className={inputCls} value={emp.phone} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} /></Field>
                    <Field label="Job Title"><input className={inputCls} value={emp.title} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} /></Field>
                    <Field label="Start Date"><input type="month" className={inputCls} value={emp.start} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} /></Field>
                    <Field label="End Date"><input type="month" className={inputCls} value={emp.end} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} /></Field>
                    <Field label="Starting Pay"><input className={inputCls} value={emp.start_pay} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, start_pay: e.target.value } : x))} /></Field>
                    <Field label="Ending Pay"><input className={inputCls} value={emp.end_pay} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, end_pay: e.target.value } : x))} /></Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <Field label="Responsibilities"><textarea rows={2} className={inputCls} value={emp.responsibilities} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, responsibilities: e.target.value } : x))} /></Field>
                    <Field label="Reason for Leaving"><textarea rows={2} className={inputCls} value={emp.reason} onChange={(e) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, reason: e.target.value } : x))} /></Field>
                  </div>
                  <Field label="May we contact this employer?"><Radio name={`emp-contact-${i}`} value={emp.contact_ok} onChange={(v) => setEmployers((a) => a.map((x, j) => j === i ? { ...x, contact_ok: v } : x))} /></Field>
                </div>
              ))}
            </div>
          </Section>

          <Section title="References" desc="Please provide three professional references.">
            <div className="space-y-4">
              {refs.map((r, i) => (
                <div key={i} className="border border-gray-200 rounded-md p-4">
                  <div className="text-sm font-semibold text-charcoal-900 mb-3">Reference {i + 1}</div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Name"><input className={inputCls} value={r.name} onChange={(e) => setRefs((a) => a.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></Field>
                    <Field label="Relationship"><input className={inputCls} value={r.relationship} onChange={(e) => setRefs((a) => a.map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))} /></Field>
                    <Field label="Company"><input className={inputCls} value={r.company} onChange={(e) => setRefs((a) => a.map((x, j) => j === i ? { ...x, company: e.target.value } : x))} /></Field>
                    <Field label="Phone Number"><input className={inputCls} value={r.phone} onChange={(e) => setRefs((a) => a.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))} /></Field>
                    <Field label="Email Address"><input className={inputCls} value={r.email} onChange={(e) => setRefs((a) => a.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} /></Field>
                    <Field label="Years Known"><input className={inputCls} value={r.years_known} onChange={(e) => setRefs((a) => a.map((x, j) => j === i ? { ...x, years_known: e.target.value } : x))} /></Field>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Additional Questions">
            <div className="space-y-4">
              <Field label="Describe your cleaning or janitorial experience."><textarea rows={3} className={inputCls} value={d.cleaning_experience} onChange={(e) => set("cleaning_experience", e.target.value)} /></Field>
              <Field label="Describe any commercial cleaning experience."><textarea rows={3} className={inputCls} value={d.commercial_experience} onChange={(e) => set("commercial_experience", e.target.value)} /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Comfortable working independently?"><Radio name="independent" value={d.work_independently} onChange={(v) => set("work_independently", v)} /></Field>
                <Field label="Comfortable working around customers/tenants/employees?"><Radio name="around" value={d.work_around_people} onChange={(v) => set("work_around_people", v)} /></Field>
                <Field label="Can you lift at least 40 pounds?"><Radio name="lift" value={d.lift_40} onChange={(v) => set("lift_40", v)} /></Field>
                <Field label="Comfortable using cleaning chemicals & equipment?"><Radio name="chem" value={d.comfortable_chemicals} onChange={(v) => set("comfortable_chemicals", v)} /></Field>
              </div>
              <Field label="Why would you like to work for United Building Maintenance and Residential Cleaning Services?"><textarea rows={3} className={inputCls} value={d.why_join} onChange={(e) => set("why_join", e.target.value)} /></Field>
              <Field label="Anything else you'd like us to know?"><textarea rows={3} className={inputCls} value={d.anything_else} onChange={(e) => set("anything_else", e.target.value)} /></Field>
            </div>
          </Section>

          <Section title="Document Uploads" desc="Upload your resume, cover letter, certifications, or other supporting documents. Accepted: PDF, DOC, DOCX, JPG, PNG (max 15MB each).">
            <label className="inline-flex items-center gap-2 border-2 border-dashed border-gray-300 hover:border-primary rounded-md px-6 py-4 cursor-pointer transition-colors w-full justify-center text-charcoal-600" data-testid="upload-label">
              {uploading ? <Loader2 className="animate-spin text-primary" size={20} /> : <Upload size={20} className="text-primary" />}
              <span className="text-sm font-medium">{uploading ? "Uploading…" : "Click to upload files"}</span>
              <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={onUpload} data-testid="file-input" />
            </label>
            {attachments.length > 0 && (
              <ul className="mt-4 space-y-2">
                {attachments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 text-charcoal-800"><FileText size={16} className="text-primary" />{a.original_filename}</span>
                    <button type="button" onClick={() => setAttachments((x) => x.filter((f) => f.id !== a.id))} className="text-gray-400 hover:text-red-500"><X size={16} /></button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Certification & Signature">
            <div className="space-y-3 text-sm text-charcoal-600">
              <p>I certify that the information provided in this application is true and complete to the best of my knowledge. I authorize United Building Maintenance and Residential Cleaning Services to contact my references and former employers. I acknowledge the privacy notice governing how my information is handled.</p>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="accent-[#22b455] mt-1" data-testid="app-consent" />
                <span>I certify the above and provide my consent. *</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <Field label="Electronic Signature (type your full name) *"><input className={inputCls} value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="app-signature" /></Field>
                <Field label="Date Signed"><input className={inputCls} value={new Date().toISOString().slice(0, 10)} disabled /></Field>
              </div>
            </div>
          </Section>

          <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-4 rounded-md transition-colors disabled:opacity-60" data-testid="app-submit">
            {submitting ? <><Loader2 className="animate-spin" size={18} /> Submitting…</> : "Submit Application"}
          </button>
        </form>
      </section>
    </div>
  );
}
